import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getPlural } from 'lib/string/pluralize'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'

interface Props {
  fromPrice: number
  duration: number
  withFlights?: boolean
  roomCount: number
  special?: boolean
  specialName?: string
  displayMemberPrice: boolean
  hasLoyaltyRoomUpgrade?: boolean
  saleUnit?: string
}

function OfferCalendarSidebarPriceBlurb(props: Props) {
  const {
    fromPrice,
    duration,
    withFlights,
    roomCount,
    special,
    specialName = 'Limited time special',
    displayMemberPrice,
    hasLoyaltyRoomUpgrade,
    saleUnit,
  } = props

  const intl = useIntl()
  const averagePrice = Math.ceil(fromPrice / (roomCount > 1 ? roomCount : duration))

  const displayUnit = saleUnit ?? 'room'

  const unit = roomCount > 1 ? (saleUnit ?? 'room') : 'night'

  const [tier] = useCurrentLoyaltyTier()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  const priceElement = <b><FormatCurrency value={fromPrice} /></b>
  const priceWithAsterisk = <b><FormatCurrency value={fromPrice} />*</b>

  let priceBlurb: React.ReactNode
  if (roomCount > 1) {
    priceBlurb = <FormattedMessage
      id="MXSSiCntCr"
      defaultMessage="{roomCount} {saleUnitLabel}, {nights, plural, one {# night} other {# nights}} from {price} total"
      description="Price blurb showing room count, sale unit, nights, and total price in calendar sidebar (multi-room)"
      values={{
        roomCount,
        saleUnitLabel: getPlural(displayUnit, roomCount),
        nights: duration,
        price: priceElement,
      }}
    />
  } else {
    priceBlurb = <FormattedMessage
      id="IPyCT10Cvo"
      defaultMessage="{nights, plural, one {# night} other {# nights}} from {price} total"
      description="Price blurb showing nights and total price in calendar sidebar (single room)"
      values={{
        nights: duration,
        price: priceElement,
      }}
    />
  }

  let priceBlurbWithFlights: React.ReactNode
  if (roomCount > 1) {
    priceBlurbWithFlights = <FormattedMessage
      id="QvhSqr21F/"
      defaultMessage="{roomCount} {saleUnitLabel}, {nights, plural, one {# night} other {# nights}} + round trip flights from {price} (2 adults)"
      description="Price blurb showing room count, sale unit, nights, flights, and total price in calendar sidebar (multi-room)"
      values={{
        roomCount,
        saleUnitLabel: getPlural(displayUnit, roomCount),
        nights: duration,
        price: priceWithAsterisk,
      }}
    />
  } else {
    priceBlurbWithFlights = <FormattedMessage
      id="twejUvQQYA"
      defaultMessage="{nights, plural, one {# night} other {# nights}} + round trip flights from {price} (2 adults)"
      description="Price blurb showing nights, flights, and total price in calendar sidebar (single room)"
      values={{
        nights: duration,
        price: priceWithAsterisk,
      }}
    />
  }

  return (
    <Group
      direction="vertical"
      gap={4}
      tabletDirection="horizontal"
      tabletHorizontalAlign="space-between"
    >
      {!withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice &&
          <BodyText weight="bold" colour="lux-plus" variant="small" as="span" style={{ marginInlineEnd: '0.25em' }}>
            {intl.formatMessage(
              {
                id: 'Ozc8LMUkZZ',
                defaultMessage: 'Showing {programName} member prices',
                description: 'Label indicating LuxPlus member prices are displayed in calendar sidebar',
              },
              { programName: LUXURY_PLUS.PROGRAM_NAME },
            )}
          </BodyText>}
        {priceBlurb}
        <CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span" style={{ marginInlineStart: '0.25em' }}>
          <FormattedMessage
            id="hI9k8+yBuW"
            defaultMessage="Average {price}/{unit}"
            description="Average price per unit in calendar sidebar price blurb"
            values={{ price: <FormatCurrency value={averagePrice} />, unit }}
          />
        </BodyText>
      </BodyText>}
      {withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice &&
          <BodyText weight="bold" colour="lux-plus" variant="small" as="span" style={{ marginInlineEnd: '0.25em' }}>
            {intl.formatMessage(
              {
                id: 'R9ragplcwT',
                defaultMessage: 'Showing {programName} member prices',
                description: 'Label indicating LuxPlus member prices are displayed in calendar sidebar with flights',
              },
              { programName: LUXURY_PLUS.PROGRAM_NAME },
            )}
          </BodyText>}
        {priceBlurbWithFlights}
        <CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span" style={{ marginInlineStart: '0.25em' }}>
          <FormattedMessage
            id="FRS72t/UcM"
            defaultMessage="Average {price}/{unit}"
            description="Average price per unit in calendar sidebar price blurb with flights"
            values={{ price: <FormatCurrency value={averagePrice} />, unit }}
          />
        </BodyText>
      </BodyText>}
      <Group direction="horizontal" gap={24}>
        {special && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidFireIcon colour="urgency" />}
        >
          {specialName}
        </CaptionTextBlock>}
        {hasLoyaltyRoomUpgrade && canLoyaltyUpgrade && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidCircleIcon colour={`tier-${tier}`} />}
        >
          {intl.formatMessage({
            id: 'Q6K89Xi9Vf',
            defaultMessage: 'Free upgrade available',
            description: 'Label indicating a free loyalty upgrade is available in sidebar price blurb',
          })}
        </CaptionTextBlock>}
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((currentState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    duration: state.duration,
    withFlights: state.flights,
    rooms: state.rooms,
    displayMemberPrice: state.displayMemberPrice,
    saleUnit: state.offer?.saleUnit,
  }
}, OfferCalendarSidebarPriceBlurb)
